import { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import { trackEvent } from "../lib/analytics";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);

function getSessionId() {
  let sid = localStorage.getItem("mahra_session_id");
  if (!sid) {
    sid = crypto.randomUUID();
    localStorage.setItem("mahra_session_id", sid);
  }
  return sid;
}

const headers = () => ({ "x-session-id": getSessionId() });

export function CartProvider({ children }) {
  const [cartCount, setCartCount] = useState(0);
  const [cartGroups, setCartGroups] = useState([]);
  const [cartTotals, setCartTotals] = useState({});
  const [discountInfo, setDiscountInfo] = useState({});
  const [cartTimerStart, setCartTimerStart] = useState(null);
  const [confirmation, setConfirmation] = useState(null);

  useEffect(() => {
    fetchCartCount();
  }, []);

  // Invalidate any active checkout session (delete pending bookings, keep cart intact)
  const invalidateCheckoutSession = useCallback(async () => {
    const stored = localStorage.getItem("mahra_checkout_state");
    if (stored) {
      try {
        const parsed = JSON.parse(stored);
        if (parsed.booking_ids?.length) {
          await axios.post(`${API}/cart/invalidate-checkout`, {
            booking_ids: parsed.booking_ids,
          }, { headers: headers() });
        }
      } catch { /* silent */ }
      localStorage.removeItem("mahra_checkout_state");
    }
  }, []);

  const fetchCartCount = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart/count`, { headers: headers() });
      setCartCount(res.data.cart_count || 0);
    } catch { /* silent */ }
  }, []);

  const fetchCart = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/cart`, { headers: headers() });
      setCartGroups(res.data.groups || []);
      setCartTotals(res.data.totals || {});
      setCartCount(res.data.cart_count || 0);
      setDiscountInfo(res.data.discount_info || {});
      setCartTimerStart(res.data.cart_timer_start || null);
      return res.data;
    } catch { return null; }
  }, []);

  const addToCart = useCallback(async (item) => {
    await invalidateCheckoutSession();
    try {
      const res = await axios.post(`${API}/cart/add`, item, { headers: headers() });
      setCartCount(res.data.cart_count || 0);
      setConfirmation(res.data.item);
      trackEvent("add_to_cart", item.outfit_id || "");
      // Microsoft Clarity — add to cart event
      if (window.clarity) {
        const ci = res.data.item || {};
        window.clarity("set", "cart_outfit", ci.outfit_name || ci.name || item.outfit_id || "");
        window.clarity("set", "cart_rental_fee", String(ci.rental_cost || ci.price_4_day || ""));
        window.clarity("event", "add_to_cart");
      }
      return res.data;
    } catch (err) {
      console.error("Add to cart failed:", err);
      // Section 5 Rev 2 — surface the server-side detail so callers don't fall
      // back to a generic "Failed to add to cart" for the pickup-only guard
      // (or any other 4xx with a human-readable message).
      const detail = err?.response?.data?.detail;
      if (detail) toast.error(detail);
      return null;
    }
  }, [invalidateCheckoutSession]);

  const removeFromCart = useCallback(async (itemId) => {
    await invalidateCheckoutSession();
    try {
      const res = await axios.delete(`${API}/cart/${itemId}`, { headers: headers() });
      setCartCount(res.data.cart_count || 0);
      return res.data;
    } catch { return null; }
  }, [invalidateCheckoutSession]);

  const updateCartItem = useCallback(async (itemId, updates) => {
    await invalidateCheckoutSession();
    try {
      const res = await axios.put(`${API}/cart/${itemId}`, updates, { headers: headers() });
      return res.data;
    } catch (err) {
      // Section 5 Rev 2 — same rationale as addToCart: bubble up the pickup-only
      // (or any other server) detail message instead of a silent null.
      const detail = err?.response?.data?.detail;
      if (detail) toast.error(detail);
      return null;
    }
  }, [invalidateCheckoutSession]);

  const clearCart = useCallback(async () => {
    await invalidateCheckoutSession();
    try {
      await axios.delete(`${API}/cart`, { headers: headers() });
      setCartCount(0);
      setCartGroups([]);
      setCartTotals({});
      setDiscountInfo({});
      setCartTimerStart(null);
    } catch { /* silent */ }
  }, [invalidateCheckoutSession]);

  const dismissConfirmation = useCallback(() => setConfirmation(null), []);

  return (
    <CartContext.Provider value={{
      cartCount, cartGroups, cartTotals, discountInfo, cartTimerStart,
      fetchCart, fetchCartCount, addToCart, removeFromCart, updateCartItem, clearCart,
      confirmation, dismissConfirmation, invalidateCheckoutSession,
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error("useCart must be used within CartProvider");
  return ctx;
}
